import { useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type Document } from "@/hooks/use-document-repo";
import { useDocumentBlob } from "@/hooks/use-document-actions";
import { formatBytes, mimeLabel, mimeColor, isPreviewable } from "@/lib/file-format";
import { formatTimestampDate } from "@/lib/dates";
import { isProtected, canInlineDownload, sourceJumpRoute } from "@/lib/document-source";
import {
  CloseIcon,
  ExternalLinkIcon,
  DownloadIcon,
  TrashIcon,
  LockClosedIcon,
} from "@/components/icons";
import { linkedRecordLabel } from "@/components/violations/linked-record-label";
import { DocumentSourceBadge } from "./document-source-badge";

interface DocumentPreviewDrawerProps {
  doc: Document | null;
  onClose: () => void;
  onDownload: (doc: Document) => void;
  onDelete: (doc: Document) => void;
}

/**
 * Right-sliding preview drawer (mockup 02). Renders the file inline via the
 * AUTHENTICATED blob hook (PDF iframe / image; non-previewable or
 * non-downloadable sources show an icon + "Download to view"), the metadata
 * column, a jump-to-source link (Attached to + external-link icon), Download,
 * and a source-aware Delete. Esc / close-icon closes. The drawer is its own
 * slide-over (not the centered
 * DialogShell) per the Drive-style mockup.
 */
export function DocumentPreviewDrawer({
  doc,
  onClose,
  onDownload,
  onDelete,
}: DocumentPreviewDrawerProps) {
  const { t } = useTranslation(["documents", "violations"]);
  const { objectUrl, isPending } = useDocumentBlob(doc);

  // Esc closes the drawer (mirrors DialogShell behavior).
  useEffect(() => {
    if (!doc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [doc, onClose]);

  if (!doc) return null;

  const label = mimeLabel(doc.content_type);
  const previewable = isPreviewable(doc.content_type) && canInlineDownload(doc.source);
  const downloadable = canInlineDownload(doc.source);
  const protectedDoc = isProtected(doc);
  const jumpRoute = sourceJumpRoute(doc);
  const context =
    linkedRecordLabel(t, doc.context_type, doc.context_label) || t("repo.columns.unfiled");

  return (
    <>
      {/* Scrim */}
      <div className="fixed inset-0 z-40 bg-gray-900/35" onClick={onClose} role="presentation" />
      {/* Drawer */}
      <aside
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-xl flex-col border-l border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800"
        role="dialog"
        aria-modal="true"
        aria-label={doc.filename}
      >
        {/* Header */}
        <div className="flex items-center gap-2.5 border-b border-gray-100 px-4 py-3 dark:border-gray-700">
          <span
            className={`flex h-6 w-6 flex-none items-center justify-center rounded-md text-[9px] font-bold text-white ${mimeColor(label)}`}
            aria-hidden="true"
          >
            {label}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-on-surface">
            {doc.filename}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("repo.preview.close")}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
          >
            <CloseIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Body: render + metadata */}
        <div className="flex flex-1 overflow-hidden">
          {/* Render */}
          <div className="flex flex-1 items-center justify-center overflow-auto bg-gray-50 p-4 dark:bg-gray-900/40">
            {previewable && isPending && (
              <p className="text-xs text-muted">{t("repo.preview.loading")}</p>
            )}
            {previewable && !isPending && objectUrl && doc.content_type === "application/pdf" && (
              <iframe
                src={objectUrl}
                title={doc.filename}
                className="h-full w-full rounded-lg border border-gray-200 bg-white dark:border-gray-700"
              />
            )}
            {previewable && !isPending && objectUrl && doc.content_type.startsWith("image/") && (
              <img
                src={objectUrl}
                alt={doc.filename}
                className="max-h-full max-w-full rounded-lg object-contain"
              />
            )}
            {(!previewable || (!isPending && !objectUrl)) && (
              <div className="flex flex-col items-center gap-2 text-center">
                <span
                  className={`flex h-12 w-12 items-center justify-center rounded-lg text-sm font-bold text-white ${mimeColor(label)}`}
                  aria-hidden="true"
                >
                  {label}
                </span>
                <p className="text-xs text-muted">
                  {downloadable ? t("repo.preview.downloadToView") : t("repo.preview.viewAtSource")}
                </p>
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="w-56 flex-none overflow-auto border-l border-gray-100 px-4 py-4 dark:border-gray-700">
            <MetaRow label={t("repo.preview.metaSource")}>
              <DocumentSourceBadge source={doc.source} />
            </MetaRow>
            <MetaRow label={t("repo.preview.metaAttachedTo")}>
              {jumpRoute ? (
                <Link
                  to={jumpRoute}
                  className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
                >
                  {context}
                  <ExternalLinkIcon className="h-3.5 w-3.5" aria-hidden="true" />
                </Link>
              ) : (
                <span className="text-on-surface">{context}</span>
              )}
            </MetaRow>
            <MetaRow label={t("repo.preview.metaType")}>
              {label} · {formatBytes(doc.size_bytes)}
            </MetaRow>
            <MetaRow label={t("repo.preview.metaUploadedBy")}>
              {doc.uploaded_by_name || "—"}
            </MetaRow>
            <MetaRow label={t("repo.preview.metaDate")} last>
              {formatTimestampDate(doc.created_at)}
            </MetaRow>
          </div>
        </div>

        {/* Footer — equal-width Download / Open + trailing Delete (mockup `.d-foot`) */}
        <div className="flex items-center gap-2 border-t border-gray-100 px-4 py-3 dark:border-gray-700">
          <button
            type="button"
            onClick={() => onDownload(doc)}
            disabled={!downloadable}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-center text-[12.5px] font-semibold text-on-primary hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <DownloadIcon className="h-4 w-4" aria-hidden="true" />
            {t("repo.preview.download")}
          </button>
          {jumpRoute && (
            <Link
              to={jumpRoute}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3.5 py-2 text-center text-[12.5px] font-semibold text-on-surface hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" />
              {t(`repo.preview.openSource.${doc.source}`)}
            </Link>
          )}
          <button
            type="button"
            onClick={() => onDelete(doc)}
            disabled={protectedDoc}
            title={protectedDoc ? t("repo.actions.protectedTooltip") : t("repo.actions.delete")}
            aria-label={t("repo.actions.delete")}
            className="flex flex-none items-center justify-center rounded-lg border border-gray-300 px-3 py-2 text-danger hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:hover:bg-red-900/20"
          >
            {protectedDoc ? (
              <LockClosedIcon className="h-4 w-4" />
            ) : (
              <TrashIcon className="h-4 w-4" />
            )}
          </button>
        </div>
      </aside>
    </>
  );
}

function MetaRow({
  label,
  children,
  last,
}: {
  label: string;
  children: React.ReactNode;
  last?: boolean;
}) {
  return (
    <div className={`py-2.5 ${last ? "" : "border-b border-gray-100 dark:border-gray-700"}`}>
      <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
        {label}
      </div>
      <div className="text-sm text-on-surface">{children}</div>
    </div>
  );
}
